import React from 'react'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import Icon from '@mui/material/Icon'
import CardHeader from '@mui/material/CardHeader'
import './common.scss'
import moment from 'moment'
import bowser from 'bowser'
import Player from '@vimeo/player'
import { format_number, getCSSVariable, setCSSVariable } from './helpers.coffee'

export capitalize = (str) ->
  return str.charAt(0).toUpperCase() + str.slice(1)

export title_label = (str) ->
  str.replace(/([A-Z])/g, ' $1').split(/[-_ ]/).map(capitalize).join(' ')

export cloneDeep = (obj) ->
  JSON.parse(JSON.stringify(obj))

export isObject = (val) ->
  return val != null && typeof val == 'object' && Array.isArray(val) == false

export fullName = (obj) ->
  return "#{obj?.first_name || ''} #{obj?.last_name || ''}".trim()

export shortName = (obj) ->
  return "#{obj.first_name || obj.last_name || ''}".trim()

# THE SERVER'S OWN SENTENCE, when it wrote one.
#
# Every form that swallowed this and substituted a house message has told a
# user something untrue. The password floor is the case that proved it (card
# 6a8df88a55f66c69d81dafe5): a parent refused for a too-short password was
# shown "Login already associated with another account." on one screen and
# "please check your internet connection" on another, so the one number they
# needed — how long the password has to be — reached nobody, and the report
# that came back was about the wrong thing entirely.
#
# The framework serializes a refusal as a BARE STRING body carrying err.message
# (schema/framework/model.coffee:1899); only a handful of allowlisted codes
# (REAUTH_REQUIRED and friends, errorCodes.coffee) send {message, code} instead.
# Read both shapes, and read them defensively: on a genuine network failure
# there is no `response` at all, and `error.response.data` on its own throws a
# TypeError inside the very catch block meant to report the problem.
#
# `fallback` is what to say when the server said nothing we can show.
# The machine-readable half of the same envelope. Only an allowlisted refusal
# carries one (schema framework/errorCodes.coffee), so this is undefined far
# more often than not — a screen branches on it to OFFER something and falls
# back to serverMessage's sentence when there is nothing to branch on.
export serverCode = (error) ->
  code = error?.response?.data?.code
  return code if typeof code == 'string' and code.length > 0
  undefined

export serverMessage = (error, fallback) ->
  data = error?.response?.data
  text =
    if typeof data == 'string' and data.length > 0 then data
    else if data?.message? then "#{data.message}"
    else if error?.message? and not error?.response? then null   # transport failure
    else null
  return fallback unless text?
  text

# For interpolating user-controlled text into a string that will be rendered as
# HTML. React escapes for you; the places that need this are the ones that
# BYPASS React — notably Highcharts tooltips built with useHTML:true, where
# neither the {series.name} template token nor a pointFormatter return value is
# escaped by the library.
export escapeHtml = (s) ->
  String(s ? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

# ── "show this once per device" ─────────────────────────────────────────────
#
# Card 6aad6d03f8ebad10784461f8. The Algebra "Small Screen Detected" warning was
# decided from scratch in SubjectList's constructor and nothing recorded that
# the student had already seen it, so it reappeared every single time the
# subject list mounted — after every game (game_layout.home routes Algebra
# students back to 'subjects'), on every reload, on every return to the screen.
# A warning that a child dismisses many times a session stops being a warning
# and becomes an obstacle.
#
# PER DEVICE, NOT PER STUDENT (owner, 2026-09-18: "Maybe one time per device so
# we can keep the one time in local storage"). A shared family phone shows it
# once, to whoever meets it first. That is deliberate: the fact being recorded
# is about the SCREEN, not about the child.
#
# WRAPPED IN try/catch BECAUSE STORAGE CAN THROW, not merely be empty: Safari
# in private mode and any browser set to block site data raise on access. The
# rule when storage is unavailable is "behave as if nothing was ever seen" —
# the warning shows every time, exactly as it does today, rather than the
# screen failing to render.
export seenOnce = (key) ->
  try
    return false unless window.localStorage?
    window.localStorage.getItem("el.seen.#{key}") == '1'
  catch
    false

# Answers whether it actually RECORDED. `window.localStorage?.setItem(...)`
# alone returned true on a window that has no localStorage at all — the
# soak-through returns undefined and the function fell to its trailing `true` —
# so the caller was told the dismissal was remembered when nothing had been
# written. The caller does not currently branch on it, but a helper whose
# answer is wrong is worse than one with no answer.
export markSeen = (key) ->
  try
    return false unless window.localStorage?
    window.localStorage.setItem("el.seen.#{key}", '1')
    true
  catch
    false

# Forget it again — the handle a test (or a support agent talking a parent
# through it) needs, so "clear storage" is not the only way back.
export forgetSeen = (key) ->
  try
    return false unless window.localStorage?
    window.localStorage.removeItem("el.seen.#{key}")
    true
  catch
    false

AVATAR_ANIMALS = [
  'elephant', 'giraffe', 'rabbit', 'dog', 'panda', 'turtle', 'tiger',
  'cat', 'alligator', 'deer', 'duck', 'koala', 'kangaroo', 'penguin'
]

AVATAR_AGE_GROUPS = [
  'baby', 'child', 'teen'
]

export avatarAgeGroupFor = (student) ->
  student_age = moment().diff(moment(student.birth_date), 'years')
  if student_age <= 6
    age_group = 'baby'
  else if student_age <= 9
    age_group = 'child'
  else
    age_group = 'teen'

export avatarAnimalFor = (student) ->
  avatar = student.currentAvatar || 'elephant'

  # convert avatar entry to animal name
  old_style = avatar.match(/student-default-avatar-([0-9]+).png$/i)
  if old_style
    # convert old avatar format to an animal name
    index = parseInt(old_style[1])
    return AVATAR_ANIMALS[index]
  else
    return avatar

export avatarFor = (student) ->
  animal    = avatarAnimalFor(student)
  age_group = student.currentAvatarAge || avatarAgeGroupFor(student)

  # construct the image path from the animal and age group
  return "#{window.config.s3_assets}images/avatars/#{age_group}/#{animal}.png"

export avatarChoicesFor = (student) ->
  # get the student's age group
  age_group = avatarAgeGroupFor(student)

  # find all of the age groups less than or equal to that group
  age_idx = AVATAR_AGE_GROUPS.indexOf(age_group)
  if age_idx > 0
    age_groups = AVATAR_AGE_GROUPS.slice(0, age_idx+1)
  else
    age_groups = [ age_group ]

  # return all choices for all valid age groups
  choices = []
  for group in age_groups
    for animal in AVATAR_ANIMALS
      choices.push { name: animal, url: "#{window.config.s3_assets}images/avatars/#{group}/#{animal}.png", age: group }
  return choices

# shuffles an array in place
export shuffle = (array) ->
  for i in [array.length - 1...0]
    j = Math.floor(Math.random() * (i+1))
    tmp = array[i]
    array[i] = array[j]
    array[j] = tmp
  return array

export Wrapper = (props) ->
  (
    <div className={"#{props.wrapperClass}-page"} style={props.style}>
      <div className={"#{props.wrapperClass}-wrap"}>
        <div className="container">
          {props.children}
        </div>
      </div>
    </div>
  )

export class ELButton extends React.Component
  constructor: (props) ->
    super props
    @handleButtonPress = @handleButtonPress.bind(this)

  handleButtonPress: (e)->
    e.preventDefault()
    @props.next(e.target)

  render: () ->
    <Button variant="contained" color="primary" onClick={@handleButtonPress}>{@props.text}</Button>

export class ProgressButton extends React.Component
  render: () ->
    if !@props.loading? || @props.loading == false
      {loading, children, ...props} = @props
      return (
        <Button {...props}>{children}</Button>
      )
    else
      return (
        <CircularProgress id="button_progress" />
      )

export class ConfirmDialog extends React.Component
  handleHide: =>
    fn = @props.onHide || @props.cancel || @props.onCancel || (() -> console.error "No hide handler given!")
    fn()

  handleCancel: =>
    fn = @props.cancel || @props.onCancel || (() -> console.error "No cancel handler given!")
    fn()

  handleConfirm: =>
    fn = @props.confirm || @props.onConfirm || (() -> console.error "No confirm handler given!")
    fn()

  render: ->
    { actions, cancel_button_text="Cancel", confirm_button_text="Confirm" } = @props

    if !actions?
      actions = []
      actions.push(<Button key="cancel" variant="text" color="secondary" onClick={@handleCancel}>{cancel_button_text}</Button>) if cancel_button_text?
      actions.push(<Button key="confirm" variant="text" color="primary" onClick={@handleConfirm}>{confirm_button_text}</Button>)

    if @props.confirm? || actions?
      <Dialog
        id={@props.dialogId}
        open={@props.visible || false}
        onClose={@handleHide}
        className="el-dialog el-above-widget"
      >
        <DialogTitle>{@props.title}</DialogTitle>
        <DialogContent className="el-dialog-content">
          {@props.confirm_text}
          {@props.children}
        </DialogContent>
        <DialogActions>
          { actions }
        </DialogActions>
      </Dialog>
    else
      return null
ConfirmDialog.defaultProps =
  dialogId: 'confirm-dialog'

# wraps DialogContainer in a way that allows for more graceful window resizes
# basically, react-md modals don't handle window resizes well, which can become
# a problem on mobile devices (especially)
export class ScrollableDialog extends React.Component
  onResize: () =>
    if @props.visible
      # get the dialog
      node = document.getElementById(@props.id)

      # if there is additional env variable padding, we should know about it
      # see: https://trello.com/c/FGz2bSxZ
      verticalEnvPadding = 0
      top = getComputedStyle(document.documentElement).getPropertyValue?('--sat') || '0px'
      btm = getComputedStyle(document.documentElement).getPropertyValue?('--sab') || '0px'
      for val in [top, btm]
        if m = val.match(/^\s*([0-9]+)px\s*$/)
          verticalEnvPadding += parseInt(m[1])

      if verticalEnvPadding != 0
        console.warn "ScrollableDialog.verticalEnvPadding = #{verticalEnvPadding}"

      # get header/content/footer (taken from react-md/src/js/Dialogs/Dialog.js)
      header  = node.querySelector('.md-title--dialog')
      content = node.querySelector('.md-dialog-content')
      footer  = node.querySelector('.md-dialog-footer')

      # account for the height of the header, footer, 48 pixel margin (from react-md), and any safe-inset padding
      padding = (header?.offsetHeight || 0) + (footer?.offsetHeight || 0) + 48
      content.style['max-height'] = "#{window.innerHeight - padding - verticalEnvPadding}px"

#  componentDidMount: () =>
#    window.addEventListener('resize', @onResize)
#
#  componentDidUpdate: (prevProps, prevState) ->
#    if prevProps.visible != @props.visible
#      # resize the window when it is first shown
#      # for some reason, this needs a bit for the header/footer to load
#      setTimeout(@onResize, 200)
#    else if prevProps.title != @props.title
#      # resize if the title changes, since that can affect sizing
#      # see: https://trello.com/c/jypJevl0
#      @onResize()
#
#  componentWillUnmount: () ->
#    # remove the resize event listener
#    window.removeEventListener('resize', @onResize)

  render: () ->
    { id, title, actions, visible, onRequestClose, className, ...rest } = @props

    # id + the width-carrying className must go on the PAPER (the dialog box),
    # not the Dialog root: on the root, .el-dialog{width:...} / #id{width:...}
    # style the full-screen Modal container and the paper stays at MUI's 600px
    # default. el-above-widget (z-index over the support widget) stays on the root.
    <Dialog
      open={visible || false}
      onClose={onRequestClose}
      className="el-above-widget"
      slotProps={{paper: {id: id, className: "#{className || 'el-dialog'}"}}}
      {...rest}
    >
      { if title
          <DialogTitle>{@props.title}</DialogTitle>
      }
      <DialogContent className="el-dialog-content">
        {@props.children}
      </DialogContent>
      { if actions? && actions.length > 0
          <DialogActions>
            { actions }
          </DialogActions>
      }
    </Dialog>
ScrollableDialog.defaultProps =
  className: "el-dialog"

# The ErrorScreen's controls are BUTTONS, not `javascript:` links — card
# 6aad8a8e6e6a1e18a090354d.
#
# React 16.9+ blocks a `javascript:` URL and logs "React has blocked a
# javascript: URL as a security precaution". The crash reporter's hooks then
# reported that log — FROM THE ERROR SCREEN ITSELF — so the one thing every
# crashing session was guaranteed to do was spend part of the alert budget on a
# warning about the crash screen's own markup. Two of the first nineteen
# production CrashLog rows were this (fingerprint 67f1c4cb).
#
# These were never links: all three carry an onClick and go nowhere. A button
# is what they are. The style keeps them looking exactly as they did.
LINK_BUTTON =
  background: 'none'
  border: 'none'
  padding: 0
  font: 'inherit'
  color: '#337ab7'
  textDecoration: 'underline'
  cursor: 'pointer'

export class ErrorScreen extends React.PureComponent
  constructor: (props) ->
    super(props)
    @state = showError: false

  render: () ->
    # get browser information
    bowserRes = bowser.getParser(window.navigator.userAgent).parse().parsedResult

    <div className="loading_screen">
      <div className="loading_window error_window">
        <section className="centered">
          <img src="#{window.config.s3_assets}images/logo-dark-text.png" />
        </section>
        <hr/>
        { @props.children }
        { if @props.showVersionInfo
            <p className="loading_text">
              { if window.app?.isApp()
                  <>You are using the app</>
                else if bowserRes.browser?
                  <>Your browser is <strong>{[bowserRes.browser.name, bowserRes.browser.version].filter(Boolean).join(' ')}</strong></>
              }
              { if (window.app?.isApp() || bowserRes.browser?) && bowserRes.os? then <br/> }
              { if bowserRes.os?
                  <>Your operating system is <strong>{[bowserRes.os.name, bowserRes.os.version].filter(Boolean).join(' ')}</strong></>
              }
            </p>
        }
        <p className="loading_text">
          Thank you, and we apologize for the inconvenience.
        </p>
        { ### A WAY OUT, not just an apology — card 6aab772ced72ee7fceb166f3.
            The commonest way onto this screen is a single transient aborted
            request; the boot read now retries once by itself, and when that
            second read fails too the honest next step is still a reload. It was
            available only as "Clear Local Storage & Reload", buried behind
            "Show Error Details" and present only when an error object was
            passed — so the person in front of the screen, usually a child, had
            no visible way off it. A plain reload keeps the session. ### }
        <p className="centered">
          <button type="button" className="button" onClick={() -> window.location.reload()}>
            Reload
          </button>
        </p>
        { if @props.error
            <React.Fragment>
            <hr/>
            {  if @state.showError
                  <div>
                    <button type="button" style={LINK_BUTTON} onClick={() => @setState {showError: false}}>Hide Error Details</button>
                    <pre style={whiteSpace: 'pre-wrap', fontSize: '12px'}>
                      { @props.error.toString() }
                      { "\n" }
                      { @props.error.stack || '' }
                    </pre>
                    <hr/>
                    <button type="button" style={LINK_BUTTON} onClick={() ->
                      localStorage.clear()
                      window.location.reload()
                    }>Clear Local Storage &amp; Reload</button>
                  </div>
                else
                  <div>
                    <button type="button" style={LINK_BUTTON} onClick={() => @setState {showError: true}}>Show Error Details</button>
                  </div>
            }
            </React.Fragment>
        }
      </div>
    </div>

export class LoadingScreen extends React.Component
  componentDidMount: () ->
    # iOS App Hack :: keyboard may not have been dismissed
    window.scrollTo(0, 0)

  render: () ->
    # assumed to be caught by the surrounding ErrorBoundary
    throw @props.error if @props.error

    <div className="loading_screen">
      <div className="loading_window">
        <section className="centered">
          <img src="#{window.config.s3_assets}images/logo-dark-text.png" />
        </section>
        <hr/>
        <section className="centered">
          <div style={display: "inline-block"}>
            <img src="#{window.config.s3_assets}images/loading_icon.gif" className="spinning" />
            <span className="loading_text" style={fontSize: '20px'}>{window.t?("Loading App....") || "Loading App...."}</span>
          </div>
        </section>
      </div>
    </div>

# on some desktop Safari browsers, the <Tabs> container's last tab does not
# render this properly.  one solution is to add a phantom last child, which is
# not easily reachable.
# see: https://trello.com/c/zYx3zNRR and https://trello.com/c/5djptieN
export lastTabHack = () ->
  if !window.app.isTouchDevice() && window.app.isSafari()
    <div/>
  else
    undefined

# Stripe REMOVED `current_period_end` from the Subscription object in API
# version 2025-03-31.basil and moved it onto each subscription ITEM. The server
# stores the raw Stripe customer blob on Account.customer, so the subscription
# objects this client renders no longer carry the top-level field either, and
# `moment((subscription.current_period_end || subscription.trial_end) * 1000)`
# was rendering "January 1st, 1970" as the next payment date on every no-trial
# plan. Mirrors common/stripePeriod.coffee on the server side — kept as a
# separate copy because the client bundle does not reach across into common/.
# Returns a moment, or null when the subscription carries no period at all.
# Card 6a812738157b2ba03490a4ab.
export subscriptionPeriodEnd = (subscription) ->
  return null unless subscription?
  usable = (v) -> typeof v is 'number' and isFinite(v) and v > 0
  ends = (i?.current_period_end for i in (subscription.items?.data or []))
  ends = (e for e in ends when usable(e))
  return moment(Math.max(ends...) * 1000) if ends.length > 0
  # Pre-basil subscription blobs persisted on older accounts.
  return moment(subscription.current_period_end * 1000) if usable(subscription.current_period_end)
  return moment(subscription.trial_end * 1000) if usable(subscription.trial_end)
  null

# WHICH CARD TO NAME on a "card on file" line. Card 6a83bceeaab51c7ff73279b5.
#
# Every such line used to read `customer.sources.data[0]` directly. A
# PaymentMethod — what the SetupIntent flow attaches — never joins `sources`,
# so after a successful card update the screens went on naming the OLD legacy
# card, and an account that never had a legacy source had nothing to name.
#
# The server now stamps `customer.default_card` ({brand, last4, exp_*}) on
# every customer it retrieves, resolved in Stripe's own charge order
# (subscription default payment method, then the customer's, then the legacy
# source) — see common/stripeCustomer.coffee. This prefers that summary and
# falls back to `sources.data[0]` for a customer blob persisted before that
# change and for accounts still on the legacy path.
#
# Returns null when there is genuinely nothing to name, which is a different
# answer from "a card is on file but I cannot describe it" — callers say so.
export cardOnFile = (customer) ->
  return null unless customer?
  # A method that is not a card (Stripe Link) is summarised by `type`, with no
  # last4 (card 6aa4b6c8f57a028d25a93d3d).
  return customer.default_card if customer.default_card?.last4? or customer.default_card?.type?
  customer.sources?.data?[0] ? null

# THE LINE FOR A PAYMENT METHOD THAT IS NOT A CARD (card 6aa4b6c8f57a028d25a93d3d).
# cardOnFile hands back {type, email} for one — Stripe Link above all, since its
# "Save my information" is on by default at checkout. Naming it beats "We
# currently have a card on file", which named nothing; with nothing resolvable
# on file at all, that old sentence is kept as it was.
export NonCardPaymentMethod = ({method}) ->
  if method?.type == 'link'
    <p>
      You are paying through <strong>Stripe Link</strong>{if method.email? then <React.Fragment> (<strong>{method.email}</strong>)</React.Fragment>}.
    </p>
  else if method?.type?
    <p>
      You are paying with <strong>{title_label(method.type)}</strong> through Stripe.
    </p>
  else
    <p>
      We currently have a card on file with Stripe.
    </p>

# THE SCHOLARSHIP LEVELS, by income band. Card 6aa6b57447c945dd8596e726.
#
# One list, because three screens read it and they must agree: the enrollment
# application quotes the discount from it, and the cancellation survey decides
# from it whether a family qualifies for an INCREASE. It mirrors the coupon
# order in Account.scholarship_for on the server (`coupons` reversed, indexed
# by income - 1, "as of this writing, levels are 30, 40, 60, 70, 80"); the
# bundle cannot import common/, so the tests repo pins that the two agree.
# Change them together.
export SCHOLARSHIP_LEVEL =
  level1: 80
  level2: 70
  level3: 60
  level4: 40
  level5: 30

# The percent a family on this income band pays under, or null when there is no
# band for it.
export scholarshipPercentFor = (income) ->
  return null unless income?
  SCHOLARSHIP_LEVEL["level#{income}"] ? null

# The percent the enrollment application QUOTES a login — which must be the
# percent the server then BILLS (card 6aaddfb1bf4219e77902d925). A scholarship
# login with no band on file (the wizard default), or a band past the last,
# gets the LAST coupon in Account.scholarship_for: band 5, 30%. The wizard used
# to quote 20% here while the family was billed 30%; the owner ruled the
# billing right (2026-09-18).
export scholarshipQuotePercentFor = (login) ->
  return 0 unless login?.scholarship
  scholarshipPercentFor(login.income) ? SCHOLARSHIP_LEVEL.level5

# The NEXT band up — the one an increase would move them to — or null when they
# are already at the top (or we do not know their band).
export nextScholarshipPercentFor = (income) ->
  return null unless income? and income > 1
  # Their OWN band has to exist too: an income we have no band for is an income
  # we cannot reason about, and offering it band 5 would be a guess.
  return null unless scholarshipPercentFor(income)?
  scholarshipPercentFor income - 1

# The scholarship coupon on file (card 6aa4d38e1c031a8daf2e49b9). The server
# resolves it and stamps `discount_coupon` on the customer
# (common/stripeCustomer.coffee): the Stripe API upgrade removed
# `customer.discount`, and a subscription's `discounts` arrive as ids. The
# singular read stays for a blob persisted before the upgrade.
export discountCouponOf = (customer) ->
  return null unless customer?
  return customer.discount_coupon if customer.discount_coupon?
  customer.discount?.coupon ? null

export format_currency = (amount, code) ->
  locale = window.app.browserLocale

  # clip sublocale from english, this is to disambiguate CAD and AUD from USD
  # see: https://trello.com/c/yIwNAcT1
  locale = 'en' if locale?.match(/^en-/)

  amount.toLocaleString(locale, {style: 'currency', currency: code})

export hide_agent_widget = () ->
  el = document.getElementById('el-agent-widget-frame')
  el?.style.display = 'none' if el?

export unhide_agent_widget = () ->
  return if window.app?.isApp()
  el = document.getElementById('el-agent-widget-frame')
  el?.style.display = '' if el?

export hack_enable_speech_synthesis = () ->
  if window.speechSynthesis?
    console.log "Running empty speech..."
    console.warn "empty speech: warning: speech synthesis is already speaking!" if window.speechSynthesis.speaking
    empty = new SpeechSynthesisUtterance(' ')
    empty.onend = () -> console.log "Empty speech ended"
    empty.onerror = (err) ->
      console.error "Empty speech ended with error!"
    window.speechSynthesis.speak(empty)

# common comparator for a variety of javascript types (used for sorting data tables)
export compare = (e1, e2) ->
  if !e1? and !e2?
    return 0
  else if !e1?
    return -1
  else if !e2?
    return 1
  else if typeof(e1) == 'string' and typeof(e2) == 'string'
    return e1.localeCompare(e2)
  else if typeof(e1) == 'number' and typeof(e2) == 'number'
    return e1 - e2
  else
    console.warn "cannot compare (#{e1}) to (#{e2})"
  return 0

export setThemeColor = (type, hex) ->
  if hex.match(/^#?[0-9a-fA-F]{6}$/)
    # convert hex into r,g,b
    hex = hex.slice(1) if hex.startsWith('#')
    [r, g, b] = [0, 2, 4].map((i) -> parseInt(hex.slice(i,i+2), 16))

    # level converter
    colorForLevel = (r, g, b, level) ->
      # take a weighted average between the color (500), white (0), and black (1000)
      if level > 500
        ratio = (1000 - level) / 500
        r = Math.trunc(ratio * r)
        g = Math.trunc(ratio * g)
        b = Math.trunc(ratio * b)
      else
        ratio = level / 500
        r = Math.trunc(ratio*r + (1-ratio)*255)
        g = Math.trunc(ratio*g + (1-ratio)*255)
        b = Math.trunc(ratio*b + (1-ratio)*255)

      return [r,g,b]

    # hex converter
    formatHex = (r,g,b) ->
      return "##{[r,g,b].map((v) -> v.toString(16).padStart(2, '0')).join('')}"

    # set the variables
    # -- transparencies
    for [suffix, level, opacity] in [['clear',500,0],['light',500,0.12],['dark',500,0.26],['very-dark',800,0.6]]
      [lr, lg, lb] = colorForLevel(r,g,b,level)
      setCSSVariable("--#{type}-trans-#{suffix}", "rgba(#{lr},#{lg},#{lb},#{opacity})")
    # -- levels
    for level in [50, 100, 200, 300, 400, 500, 600, 700, 800, 900]
      setCSSVariable("--#{type}-#{level}", formatHex(...colorForLevel(r, g, b, level)))
    return true
  else
    console.error "setCSSVariables: invalid color format"
    return false

# an HOC that updates a component whenever the device (as per react-md) changes
# injects a prop 'device' of ['desktop', 'tablet', 'phone'] based on the screen size
REACT_MD_DESKTOP_MIN = 840
REACT_MD_TABLET_MIN  = 600
export withDevice = (WrappedComponent) ->
  class extends React.Component
    constructor: (props) ->
      super(props)
      @state = {device: @currentDevice()}

    componentDidMount: () ->
      window.addEventListener('resize', @onResize)

    componentWillUnmount: () ->
      window.removeEventListener('resize', @onResize)

    currentDevice: () =>
      width = window.innerWidth
      if width < REACT_MD_TABLET_MIN
        return 'phone'
      else if width < REACT_MD_DESKTOP_MIN
        return 'tablet'
      else
        return 'desktop'

    onResize: () =>
      device = @currentDevice()
      if device != @state.device
        console.debug "Detected device change from #{@state.device} to #{device}"
        @setState { device }

    render: () ->
      <WrappedComponent device={@state.device} {...@props} />

export class VimeoTag extends React.PureComponent
  componentDidMount: () ->
    @breakpoint = Math.trunc(window.innerWidth / 75) * 75
    window.addEventListener('resize', @onResize)

    @player = new Player(@props.id)

    if @props.autoplay
      @player.play().catch (err) =>
        if err.name in ['AbortError', 'NotAllowedError']
          console.warn "VimeoTag :: Autoplay disallowed"
        else
          console.error "VimeoTag :: Error playing video:"
          console.dir err

        # if this is Safari, it will hide the preview image after a failed auto-
        #   play, unless we manually reset the whole widget
        #   see: https://trello.com/c/MMFpcQ7S
        # this method is also poorly named... the docs say it just resets the
        #   current state, so afaik there is no data-loss penalty
        if window.app.isSafari()
          @player.unload()

    if @props.onEnd
      @player.on 'ended', @props.onEnd

    @player.on 'fullscreenchange', @onFullScreenChange

  componentWillUnmount: () ->
    window.removeEventListener('resize', @onResize)

  onResize: () =>
    # note 1: the /* 60 is so we don't resize too often
    # note 2: it also divides the mobile/tablet breakpoints, so it updates correctly on boundaries
    nextBreakpoint = Math.trunc((window.innerWidth) / 60) * 60
    if nextBreakpoint != @breakpoint
      @breakpoint = nextBreakpoint
      @resizeiframe()

  onFullScreenChange: ({fullscreen}) =>
    # apparently, Safari handles z-index poorly for this thing (see https://trello.com/c/Hr4y0Hv4)
    #   therefore, if we notice full screen, we go for some CSS magic
    if fullscreen
      console.debug "VimeoTag: entering fullscreen..."
      document.body.classList.add('el-vimeo-fullscreen')
    else
      console.debug "VimeoTag: exitting fullscreen..."
      document.body.classList.remove('el-vimeo-fullscreen')

  componentDidUpdate: (prevProps) ->
    if @props.width != prevProps.width
      @resizeiframe()

  resizeiframe: () =>
    # to actually update the width, we need to hack the iframe once this has been
    #   mounted
    div = document.getElementById(@props.id)
    iframe = div.firstChild
    if iframe?.tagName?.toLowerCase() == 'iframe'
      # get the next width
      width = @getWidth()

      # calculate the aspect ratio, if width and height are non-zero
      # note: this is cached instead of recalculated, as we get weird round-off
      #       errors otherwise
      if !@aspectRatio? && iframe.width? && iframe.height? && parseInt(iframe.width) && parseInt(iframe.height)
        @aspectRatio = iframe.height / iframe.width

      # retain the aspect ratio, if possible
      iframe.width = "#{parseInt(width)}"
      iframe.height = "#{Math.trunc(@aspectRatio * width)}" if @aspectRatio
    else
      console.warn "VimeoTag :: no iframe found!"

  getWidth: () =>
    { width, widthPerc, maxWidth, minWidth, tabletWidthPerc, phoneWidthPerc } = @props

    # determine which layout we're in (match react-md checks)
    innerWidth = window.innerWidth
    layout = 'desktop'
    layout = 'tablet' if innerWidth < REACT_MD_DESKTOP_MIN
    layout = 'phone' if innerWidth < REACT_MD_TABLET_MIN

    # use different perc's based on layout
    width = Math.trunc(innerWidth * widthPerc) if widthPerc?
    width = Math.trunc(innerWidth * tabletWidthPerc) if tabletWidthPerc? && layout == 'tablet'
    width = Math.trunc(innerWidth * phoneWidthPerc) if phoneWidthPerc? && layout == 'phone'

    # box to min/max width after percentages
    width = Math.max(minWidth, width) if minWidth?
    width = Math.min(maxWidth, width) if maxWidth?

    return width

  render: () ->
    { url, width, className, widthPerc, phoneWidthPerc, tabletWidthPerc, maxWidth, minWidth, autoplay, onEnd, ...rest } = @props

    <div
       className="#{className || ''} el-vimeo"
       {...rest}
       data-vimeo-url={url}
       data-vimeo-width={@getWidth()}
    />

export class LoadingMessage extends React.PureComponent
  render: () ->
    message = @props.message || (window.t?("Loading...") || "Loading...")

    <div className="el-loading-area">
      <div className="indicator">
        <CircularProgress id="loading-indicator" centered />
      </div>
      <div className="message">
        { message }
      </div>
    </div>

export class StyledCardTitle extends React.PureComponent
  render: () ->
    <CardHeader
      className="el-card-title"
      title={<h1 style={{fontFamily: 'Montserrat, sans-serif', fontSize: '28px', fontWeight: 700, lineHeight: '39.2px', margin: 0}}>{@props.title}</h1>}
      subheader={<h5 style={{fontFamily: 'Montserrat, sans-serif', fontSize: '11.62px', fontWeight: 700, lineHeight: '16.3px', margin: 0}}>{@props.subtitle}</h5>}
    />

export knowledgeBaseUrl = (article='', isRedirect) ->
  # The knowledge base is now served from this site's own CMS at /knowledgebase,
  # so there is no environment switch left to make — every deployment links at
  # its own KB rather than at the old marketing/webflow hosts.
  url = "/knowledgebase"

  # split off the hash, if given
  if (hashIndex = article.indexOf('#')) != -1
    hash = article.slice(hashIndex)
    article = article.slice(0, hashIndex)
  else
    hash = undefined

  # add the article, if given. No trailing slash — the CMS show route accepts
  # either, but the canonical URL it emits has none, so match it.
  if article
    url = "#{url}/#{article}"

  # url parameters
  params = []
  # -- let it know if we're coming from the app
  params.push "isApp=#{window.app.isApp()}"
  # -- if we're opening a new window, "back to app" should just close this
  params.push "closeOnBack=true" if !isRedirect
  # -- add the params to the url
  url += "?#{params.join('&')}" if params.length > 0

  # add the hash back
  url += hash if hash?

  return url

export openKnowledgeBase = (article) ->
  # open a window, and redirect in current window if that fails (effectively used to see if we're in app)
  openUrl = knowledgeBaseUrl(article, false)
  unless window.app.isApp()
    console.log "openKnowledgeBase: attempting to open new window to \"#{openUrl}\"..."
    obj = window.open(openUrl, "_blank")
  unless obj?
    redirectUrl = knowledgeBaseUrl(article, true)
    console.log "openKnowledgeBase: failed to open new window. redirecting to \"#{redirectUrl}\"..."
    window.location = redirectUrl

SUPPORT_EMAIL = "support@elephantlearning.com"
export supportLink = (text=SUPPORT_EMAIL) =>
  <a href="mailto:#{SUPPORT_EMAIL}">{text}</a>

# Shared user-visible rejection path for portal mutations and data loads
# (card 6a6baec9683d2463668f1eee). The tenant hardening added deliberate
# 401/404 refusals, but the callers had no .catch — dialogs stayed open and
# buttons looked dead. Authorization refusals explain that access changed and
# suggest a refresh; anything else gets a generic error. `cleanup` runs
# FIRST so pending dialog state resets even if the toast throws. Never turns
# a refusal into apparent success.
export apiErrorMessage = (err) ->
  status = err?.status ? err?.statusCode ? err?.response?.status
  if status in [401, 403, 404]
    "You no longer have access to do that. The student or folder may have moved, or a share may have ended — refresh the page and try again."
  else
    "Something went wrong. Please try again, and refresh the page if it keeps happening."

export notifyApiError = (err, cleanup) ->
  console.error "API call failed:"
  console.dir err
  cleanup?()
  msg = apiErrorMessage(err)
  if window.app?.emitter?.emit?
    window.app.emitter.emit 'global toast', msg
  else
    window.alert msg
  return

export inAlgebra = (student) ->
  "Algebra" in (student?.advanced_tags || [])

# react-md v3 has Switch return a string, which is truly obnoxious, since we
#   really want a boolean. this wraps this to fake a return as a boolean
export class BooleanSwitch extends React.Component
  handleChange: (e) =>
    onChange = @props.onChange || (() -> console.error "No handleChange given!")

#    console.debug "BooleanSwitch: handleChange:"
#    console.dir e.target
#    ["name", "value", "checked"].map (k) -> console.log k, e.target[k], typeof(e.target[k])

    { name, value, checked } = e.target

    return onChange({target: {name, value: checked, checked}})

  render: () ->
    { label, ...switchProps } = @props

    switchElement = <Switch {...switchProps} onChange={@handleChange} />

    if label
      <FormControlLabel
        control={switchElement}
        label={label}
      />
    else
      switchElement

export class ArrowButton extends React.Component
  render: () ->
    { className, isOpen, ...rest } = @props
    icon = if isOpen then <Icon>expand_more</Icon> else <Icon>chevron_right</Icon>

    <div
      {...rest}
      style={display: 'flex', justifyContent: 'center', cursor: 'pointer'}
      className={className}
    >
      { icon }
    </div>